import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { useTranslation } from "react-i18next";

import {
    ARCHIVED_ASSET_STATUS,
    ASSET_PREVIEW_STATUS,
    ASSET_THUMBNAIL_CARD_CONTAINER_SIZE,
    ASSET_TYPES,
    COMMON_STATUS,
    GROUPING_VIEW_TYPE,
    LIST_VIEW_TYPE,
    MEDIA_TYPES,
} from "../../constants";
import { MediaImage } from "./components";
import { Link } from "react-router-dom";
import convertToByte from "../../cores/convertToByte";
import { AssetStatusArchived, AssetStatusArchiving, AssetStatusRestoring } from "./AssetStatus";
import { siteMode } from "../../cores/siteMode";
import compareVersions, {
    VERSION_CM_13766_SHARES_V3,
    VERSION_CM_13925_OWNER_LOCK,
    VERSION_CM_14143_REQUEST,
    VERSION_CM_14205_ASSET_STARRED,
    VERSION_CM_14830_SEPARATION_OF_OWNER_AND_CREATOR,
} from "@cores/version";
import menuService from "@services/menuService";
import { Box, Card, CardContent, CardHeader, Checkbox, Divider, Grid, Skeleton, Stack, Typography } from "@mzc-pdc/ui";
import theme from "@styles/theme";
import { IconAi, IconDrm, IconLink, IconLock, IconMoreHoriz, IconNew, IconStar, IconVoice } from "@mzc-cloudplex/icons";
import AssetTypeFlag from "@components_v2/icon-type/asset/asset-type-flag";
import { alpha, CardActionArea } from "@mui/material";
import MediaImageWrapper from "@routes/asset/components/media-image-wrapper";
import useCheckPermission from "@hooks/useCheckPermission";
import { Menu } from "@components_v2/menu";
import countDaysBetweenDates from "@cores/countDaysBetweentDates";
import dayjs from "dayjs";
import countDaysBetweentDates from "@cores/countDaysBetweentDates";
import { CircledIcon } from "@components_v2/icon";
import AssetSearchIndexStatus, { isAssetSearchIndexRetryable } from "@components_v2/asset-search-index-status";
import AssetProxyStatus, { getAssetProxyStatus, isAssetProxyStatusNoteworthy } from "@components_v2/asset-proxy-status";
import { EllipsisTypography } from "@components_v2/ellipsis-tooltip";

const AssetCard = ({
    asset,
    checkable,
    checked,
    draggable,
    thumbnailCrop,
    isProxyVideoEnabled,
    onDragStartItem = function () {},
    onDragEndItem = function () {},
    onChangeCheck = function () {},
    onClickDeleteAsset = function () {},
    onClickCreateJob = function () {},
    onClickRow = function () {},
    onDoubleClickRow = function () {},
    renderTools,
    hiddenCreateJobButton,
    onToggleModal = function () {},
    onClickName,
    onClickDownload = function () {},
    onClickStarred = function () {},
    onClickOwnerLock = function () {},
    onClickReindex = function () {},
    showReindex = false,
}) => {
    const propertyPanelTargetId = useSelector(({ propertyPanel }) => propertyPanel.targetId);
    const stage = useSelector(({ stage }) => stage);
    const { t } = useTranslation();
    const history = useHistory();
    const { spaceId, projectId } = menuService.parse(history.location.pathname);
    const isLibrary = siteMode.get() === "CMS";

    const ASSETS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Assets" });
    const VIDEOS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Videos" });
    const PHOTO_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Photo" });
    const SHARES_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Shares" });
    const DOWNLOAD_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Download" });

    const [isShowOptions, setIsShowOptions] = useState(false);
    const [isShowImagePreviewModal, setIsShowImagePreviewModal] = useState(false);
    const [isHover, setIsHover] = useState(false);

    const isActive = asset.id === propertyPanelTargetId;
    const isInactive = asset.status === COMMON_STATUS.INACTIVE;
    const isCms = siteMode.get() === "CMS";
    const disabledCreateJobButton = (asset) => {
        if (
            asset.status === COMMON_STATUS.INACTIVE ||
            asset.mediaType !== MEDIA_TYPES.VIDEO ||
            asset.type === ASSET_TYPES.DASH ||
            asset.type === ASSET_TYPES.HLS
        )
            return true;

        return false;
    };

    const disabledCreateJob = disabledCreateJobButton(asset);

    const [isDragging, setIsDragging] = useState(false);

    const assets = useSelector(({ assets }) => assets);

    const onClickCard = (assetId, projectId) => {
        onClickRow(assetId, projectId);
    };
    const onDoubleClickCard = (assetId, projectId) => {
        if (isShowImagePreviewModal) {
            return;
        }

        onDoubleClickRow(asset);
    };

    const handleShowImagePreviewModal = useCallback(() => {
        setIsShowImagePreviewModal(true);
    }, [isShowImagePreviewModal]);

    const handleCloseImagePreviewModal = useCallback(() => {
        setIsShowImagePreviewModal(false);
    }, [isShowImagePreviewModal]);

    const getSkeleton = ({ type, width, height }) => (
        <Skeleton
            variant={type}
            animation={"wave"}
            width={width}
            height={height}
            sx={{ backgroundColor: alpha("#dfdfee", 0.5) }}
        />
    );

    // 프록시 추출 상태(표시 전용 파생). 영상 자산이 아니거나 프록시가 없으면 null (DAM-457)
    const proxyStatus = useMemo(() => getAssetProxyStatus(asset), [asset]);
    // 아카이브 계열 상태는 같은 좌하단 자리를 쓰므로 겹치지 않게 우선권을 준다.
    const isArchiveRelated = [
        ARCHIVED_ASSET_STATUS.ARCHIVED,
        ARCHIVED_ASSET_STATUS.ARCHIVING,
        ARCHIVED_ASSET_STATUS.RESTORING,
    ].includes(asset.archiveStatus);

    const assetDetailPath = useMemo(() => {
        if (onClickName) return onClickName(asset);
        else
            return `${menuService.paths.assetsAssetDetail(spaceId, projectId, asset.id)}?projectId=${
                asset.project?.id
            }`;
    }, [spaceId, projectId, asset]);

    const hasDownloadPermissionByShare = useMemo(() => {
        const isExpired = asset?.shareInfo?.expiresAt
            ? countDaysBetweenDates({ toDate: dayjs(asset?.shareInfo?.expiresAt) }) < 0
            : false;

        const isExceedDownloadCount =
            asset?.shareInfo?.maxDownloads > 0 && asset?.shareInfo?.maxDownloads <= asset?.shareInfo?.downloadCount;

        return asset?.shareInfo && !(isExpired || isExceedDownloadCount);
    }, [asset]);

    const isOwner = useMemo(() => {
        return (
            (compareVersions(stage.version, VERSION_CM_14830_SEPARATION_OF_OWNER_AND_CREATOR) < 0
                ? asset.createdBy?.id
                : asset.owner?.id) === stage.user?.id
        );
    }, [stage, asset]);

    const disabledByOwnerLock = useMemo(() => {
        return asset?.isOwnerLocked && !isOwner;
    }, [asset, isOwner]);

    const hiddenDownloadButton = useMemo(() => {
        return (
            (!asset?.shareInfo || !hasDownloadPermissionByShare) &&
            (!DOWNLOAD_PERMISSIONS.DOWNLOAD || disabledByOwnerLock)
        );
    }, [asset, hasDownloadPermissionByShare, DOWNLOAD_PERMISSIONS, disabledByOwnerLock]);

    return (
        <Card
            sx={{
                position: `relative`,
                width: `202px`,
                boxShadow: `none`,
                ".btn-dropdown": {
                    opacity: 0,
                    top: 10,
                    right: 10,
                    ".MuiIconButton-root": {
                        backgroundColor: alpha(theme.palette.constant.black, 0.6),
                    },
                    "&.dropdown-open": {
                        opacity: 1,
                        ".MuiIconButton-root": {
                            backgroundColor: alpha(theme.palette.constant.black, 0.92),
                        },
                    },
                },
                "&:hover .btn-dropdown": {
                    opacity: 1,
                    ".MuiIconButton-root:hover": {
                        backgroundColor: alpha(theme.palette.constant.black, 0.8),
                    },
                },
            }}
            draggable={draggable}
            onDragStart={(e) => {
                e.stopPropagation();
                setIsDragging(true);
                if (onDragStartItem) onDragStartItem(e, asset);
            }}
            onDragEnd={(e) => {
                e.stopPropagation();
                setIsDragging(false);
                if (onDragEndItem) onDragEndItem(e, asset);
            }}
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => {
                setIsHover(false);
                setIsShowOptions(false);
            }}
        >
            <CardHeader
                action={
                    <>
                        <Grid sx={{ mb: theme.spacing(1) }}>
                            {checkable ? (
                                <Checkbox
                                    color={"greySecondary"}
                                    checked={checked}
                                    disabled={asset.state === "DRAFT"}
                                    onChange={() => {
                                        if (onChangeCheck) onChangeCheck(asset);
                                    }}
                                />
                            ) : (
                                <div></div>
                            )}
                        </Grid>
                        {isHover && (
                            <Menu
                                menuMinWidth={160}
                                placement={"right"}
                                type={"icon"}
                                label={<IconMoreHoriz size={16} />}
                                size={"small"}
                                list={[
                                    {
                                        label: t(`common::label::Share link`, `Share link`),
                                        onClick: () => {
                                            onToggleModal("shareLink", true, asset);
                                        },
                                        disabled:
                                            asset.state === "DRAFT" ||
                                            !SHARES_PERMISSIONS.EXTERNAL ||
                                            disabledByOwnerLock,
                                        hidden: isCms,
                                        clickClose: true,
                                    },
                                    {
                                        label: t(`common::label::Share request`, `Share request`),
                                        onClick: () => {
                                            onToggleModal("requestShare", true, asset);
                                        },
                                        disabled: asset.state === "DRAFT" || !SHARES_PERMISSIONS.INTERNALREQUEST,
                                        hidden: !(
                                            !isCms && compareVersions(stage.version, VERSION_CM_13766_SHARES_V3) < 0
                                        ),
                                        clickClose: true,
                                    },
                                    {
                                        label: t(`common::label::Copy to`, `Copy to`),
                                        divider: true,
                                        onClick: () => {
                                            onToggleModal("copyCategory", true, asset);
                                        },
                                        disabled:
                                            asset.state === "DRAFT" || !ASSETS_PERMISSIONS.EDIT || disabledByOwnerLock,
                                        hidden: !(
                                            !isCms &&
                                            assets?.isActiveView?.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY &&
                                            assets.hierarchyType === GROUPING_VIEW_TYPE.CATEGORY
                                        ),
                                        clickClose: true,
                                    },
                                    {
                                        label: t(`common::label::Move`, `Move`),
                                        divider: !(
                                            assets?.isActiveView?.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY &&
                                            assets.hierarchyType === GROUPING_VIEW_TYPE.CATEGORY
                                        ),
                                        onClick: (e) => {
                                            if (assets.hierarchyType === GROUPING_VIEW_TYPE.CATEGORY) {
                                                onToggleModal("moveCategory", true, asset);
                                            } else {
                                                onToggleModal("moveFolder", true, asset);
                                            }
                                            //category, folder에 따라 분기처리가 필요
                                        },
                                        disabled:
                                            asset.state === "DRAFT" ||
                                            !ASSETS_PERMISSIONS.EDIT ||
                                            (assets.hierarchyType === GROUPING_VIEW_TYPE.CATEGORY &&
                                                disabledByOwnerLock),
                                        hidden: !(
                                            !isCms && assets?.isActiveView?.toLowerCase() === LIST_VIEW_TYPE.HIERARCHY
                                        ),
                                        clickClose: true,
                                    },
                                    {
                                        label: t(`common::label::Rename`, `Rename`),
                                        divider: assets?.isActiveView?.toLowerCase() === LIST_VIEW_TYPE.LIST,
                                        onClick: () => {
                                            onToggleModal("rename", true, asset);
                                        },
                                        disabled:
                                            asset.state === "DRAFT" || !ASSETS_PERMISSIONS.EDIT || disabledByOwnerLock,
                                        hidden: isCms,
                                        clickClose: true,
                                    },
                                    {
                                        label: t(`common::label::Download`, `Download`),
                                        divider: !isCms,
                                        onClick: () => {
                                            onClickDownload(asset);
                                        },
                                        disabled:
                                            asset.state === "DRAFT" ||
                                            (compareVersions(stage.version, VERSION_CM_14143_REQUEST) < 0 &&
                                                !DOWNLOAD_PERMISSIONS.DOWNLOAD),
                                        hidden:
                                            compareVersions(stage.version, VERSION_CM_14143_REQUEST) >= 0 &&
                                            hiddenDownloadButton,
                                        clickClose: true,
                                    },
                                    {
                                        label: t(`common::label::Request download`, `Request download`),
                                        onClick: () => {
                                            onToggleModal("requestDownload", true, asset);
                                        },
                                        disabled: asset.state === "DRAFT" || !SHARES_PERMISSIONS.INTERNALREQUEST,
                                        hidden:
                                            compareVersions(stage.version, VERSION_CM_14143_REQUEST) < 0 ||
                                            !hiddenDownloadButton,
                                        clickClose: true,
                                    },
                                    {
                                        label: t(`common::label::Request`, `Request`),
                                        onClick: () => {
                                            onToggleModal("request", true, asset);
                                        },
                                        disabled: asset.state === "DRAFT" || !SHARES_PERMISSIONS.INTERNALREQUEST,
                                        hidden: compareVersions(stage.version, VERSION_CM_14143_REQUEST) < 0,
                                        clickClose: true,
                                    },
                                    {
                                        label: t(
                                            `common::label::${asset?.myFavorite ? "Unstarred" : "Starred"}`,
                                            asset?.myFavorite ? "Unstarred" : "Starred",
                                        ),
                                        onClick: () => {
                                            onClickStarred([asset.id], asset.myFavorite);
                                        },
                                        disabled: asset.state === "DRAFT",
                                        hidden: compareVersions(stage.version, VERSION_CM_14205_ASSET_STARRED) < 0,
                                        clickClose: true,
                                    },
                                    {
                                        label: asset.isOwnerLocked
                                            ? t(`common::label::Unlock`, `Unlock`)
                                            : t(`common::label::Lock`, `Lock`),
                                        divider: false,
                                        onClick: () => {
                                            onClickOwnerLock(asset);
                                        },
                                        hidden: !(
                                            !isCms && compareVersions(stage.version, VERSION_CM_13925_OWNER_LOCK) >= 0
                                        ),
                                        disabled: asset.state === "DRAFT" || !ASSETS_PERMISSIONS.EDIT || !isOwner,
                                        clickClose: true,
                                    },
                                    {
                                        label: t(`common::label::Retry indexing`, `Retry indexing`),
                                        onClick: () => {
                                            onClickReindex(asset);
                                        },
                                        // 노출 게이트(CMS·AI 모드·AI 검색 미설정·버전)는 List.js가 판단한다.
                                        // 상태로는 숨기지 않는다 - 카드마다 항목 수가 달라지면 아래 Delete 위치가 흔들린다.
                                        hidden: !showReindex,
                                        // 실패했거나 아직 인덱싱되지 않은 자산만 재시도 대상(일괄 바와 동일 판정).
                                        disabled:
                                            asset.state === "DRAFT" ||
                                            !ASSETS_PERMISSIONS.EDIT ||
                                            !isAssetSearchIndexRetryable(asset.indexDoc),
                                        clickClose: true,
                                    },
                                    // {
                                    //     label: t(`common::label::Archive`, `Archive`),
                                    //     separate: true,
                                    //     onClick: (e) => {

                                    //     },
                                    //     enable: !isCms,
                                    // },
                                    {
                                        label: t(`common::label::Delete`, `Delete`),
                                        divider: true,
                                        onClick: () => {
                                            if (onClickDeleteAsset) onClickDeleteAsset(asset);
                                        },
                                        disabled: !ASSETS_PERMISSIONS.EDIT || disabledByOwnerLock,
                                        hidden: isCms,
                                        clickClose: true,
                                    },
                                ]}
                                useInCard={true}
                                sx={{
                                    mt: theme.spacing(1.5),
                                    mr: theme.spacing(1.5),
                                }}
                            />
                        )}
                    </>
                }
                sx={{
                    position: `absolute`,
                    width: `100%`,
                    zIndex: `1`,
                    p: 0,
                    ".MuiCardHeader-content": {
                        display: `none`,
                    },
                    ".MuiCardHeader-action": {
                        flexGrow: 1,
                        display: `flex`,
                        justifyContent: `space-between`,
                        m: 0,
                    },
                }}
            />
            <CardActionArea
                onClick={(e) => {
                    console.log(e);
                    e.stopPropagation();
                    onClickCard(asset.id, asset.project.id);
                    // if (onClickCardCover) onClickCardCover(asset.id);
                }}
                onDoubleClick={(e) => {
                    e.stopPropagation();
                    if (ASSETS_PERMISSIONS.READ) onDoubleClickCard(asset.id, asset.project.id);
                    // if (onDoubleClickCardCover) onDoubleClickCardCover(asset.id);
                }}
                sx={{ cursor: isLibrary ? "default" : "pointer" }}
            >
                <MediaImageWrapper
                    status={
                        asset.state === "DRAFT"
                            ? "IN_PROGRESS"
                            : asset.previews?.filter((p) => p.status !== ASSET_PREVIEW_STATUS.ERROR)?.[0]?.status
                    }
                    sx={{
                        display: `flex`,
                        alignItems: `center`,
                        justifyContent: `center`,
                        width: ASSET_THUMBNAIL_CARD_CONTAINER_SIZE,
                        height: ASSET_THUMBNAIL_CARD_CONTAINER_SIZE,
                        backgroundColor: isActive
                            ? alpha(theme.palette.greySecondary.A100, 0.6)
                            : checked
                              ? alpha(theme.palette.greySecondary.A100, 0.4)
                              : alpha(theme.palette.greySecondary[50], 0.5),
                        borderRadius: `8px`,
                    }}
                >
                    <MediaImage
                        variant={"card"}
                        draggable={draggable}
                        asset={asset}
                        size={"large"}
                        thumbnailCrop={thumbnailCrop}
                        errorIconProps={{ position: "right", size: 16 }}
                        isShowImagePreviewModal={isShowImagePreviewModal}
                        handleShowImagePreviewModal={handleShowImagePreviewModal}
                        handleCloseImagePreviewModal={handleCloseImagePreviewModal}
                    />
                    {asset.archiveStatus === ARCHIVED_ASSET_STATUS.ARCHIVED && (
                        <AssetStatusArchived containerType={"archive"} status={asset.archiveStatus} />
                    )}
                    {asset.archiveStatus === ARCHIVED_ASSET_STATUS.ARCHIVING && (
                        <AssetStatusArchiving />
                    )}
                    {asset.archiveStatus === ARCHIVED_ASSET_STATUS.RESTORING && (
                        <AssetStatusRestoring />
                    )}
                    {/* 프록시 추출 상태(DAM-457) - 썸네일 좌하단. 아카이브 계열 상태가 있으면 그쪽이 우선이라 비켜준다.
                        하단 메타 행의 배지(DRM·AI·잠금·인덱싱)는 자산의 지속적 속성 자리이고 4칸으로 고정돼 있어,
                        "지금 처리 중"인 일시적 상태는 아카이빙과 같은 계열인 이 자리에 둔다.
                        전면 오버레이는 쓰지 않는다 - 썸네일은 이미 완성됐고 저화질 영상만 남은 경우
                        "이 썸네일이 준비 중"이라는 잘못된 신호가 되기 때문. */}
                    {isProxyVideoEnabled && !isArchiveRelated && isAssetProxyStatusNoteworthy(proxyStatus) && (
                        <Box
                            sx={{
                                position: "absolute",
                                left: theme.spacing(1),
                                bottom: theme.spacing(1),
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            <AssetProxyStatus status={proxyStatus.status} size={"sm"} />
                        </Box>
                    )}
                    {countDaysBetweentDates({ fromDate: asset.createdAt }) < 7 && (
                        <Box
                            sx={{
                                position: "absolute",
                                right: theme.spacing(1),
                                bottom: theme.spacing(1),
                                color: theme.palette.error.main,
                            }}
                        >
                            <IconNew size={34} viewBox={"0 0 34 16"} style={{ height: "16px" }} />
                        </Box>
                    )}
                </MediaImageWrapper>
            </CardActionArea>

            <CardContent sx={{ px: 1, "&:last-of-type": { pb: 2 } }}>
                <Stack>
                    <Grid container alignItems={"center"} flexWrap={"nowrap"} sx={{ overflow: `hidden` }}>
                        <AssetTypeFlag size={"small"} type={asset.mediaType} />
                        <Grid item xs sx={{ textOverflow: `ellipsis`, overflow: `hidden` }}>
                            <EllipsisTypography variant={"body1_500"} tooltipTitle={asset.name} noWrap>
                                <Link
                                    to={assetDetailPath}
                                    style={{ color: `inherit`, ":hover": { textDecoration: `none` } }}
                                    disabled={!ASSETS_PERMISSIONS.READ}
                                >
                                    {asset.name}
                                </Link>
                            </EllipsisTypography>
                        </Grid>
                        <Stack direction={"row"} gap={theme.spacing(0.5)}>
                            {asset.isConnected ? <IconLink size={16} /> : ""}
                            {asset.myFavorite ? <IconStar size={16} htmlColor={theme.palette.etc.orange500} /> : ""}
                        </Stack>
                    </Grid>

                    <Grid container alignItems={"center"} gap={0.5} mt={1} flexWrap={"nowrap"}>
                        {asset.state === "DRAFT" ? (
                            <>
                                <Grid item xs>
                                    <Grid container flexDirection={"row"}>
                                        {getSkeleton({ type: "rounded", width: 40, height: 18 })}
                                        <Divider flexItem orientation={"vertical"} sx={{ my: 0.5, mx: 1 }} />
                                        {getSkeleton({ type: "rounded", width: 60, height: 18 })}
                                    </Grid>
                                </Grid>
                            </>
                        ) : (
                            <>
                                <Grid item xs>
                                    <Grid container>
                                        <Typography variant={"body3_400"} color={theme.palette.text.secondary}>
                                            {asset.extensions ? asset.extensions : "-"}
                                        </Typography>
                                        <Divider
                                            flexItem
                                            orientation={"vertical"}
                                            sx={{ my: 0.5, mx: theme.spacing(0.75) }}
                                        />
                                        <Typography variant={"body3_400"} color={theme.palette.text.secondary}>
                                            {asset.size ? convertToByte(asset.size, true) : "-"}
                                        </Typography>
                                    </Grid>
                                </Grid>
                                <Stack direction={"row"} gap={theme.spacing(0.5)} sx={{ "& i.sprite + *": { ml: 0 } }}>
                                    {asset.hasDubbing && (
                                        <CircledIcon size={20} color={theme.palette.etc.purple400}>
                                            <IconVoice size={12} htmlColor={theme.palette.constant.white} />
                                        </CircledIcon>
                                    )}
                                    {asset.encryptions?.indexOf("DRM") > -1 && (
                                        <CircledIcon size={20} color={theme.palette.warning.main}>
                                            <IconDrm size={12} htmlColor={theme.palette.constant.white} />
                                        </CircledIcon>
                                    )}
                                    {asset.isAnalyzed && (
                                        <CircledIcon size={20} color={theme.palette.success.light}>
                                            <IconAi size={12} htmlColor={theme.palette.constant.white} />
                                        </CircledIcon>
                                    )}
                                    {asset.isOwnerLocked && (
                                        <CircledIcon size={20} color={theme.palette.etc.gray300}>
                                            <IconLock size={12} htmlColor={theme.palette.constant.white} />
                                        </CircledIcon>
                                    )}
                                    {asset.indexDoc?.status && (
                                        <AssetSearchIndexStatus
                                            status={asset.indexDoc.status}
                                            size={"sm"}
                                            errorCode={asset.indexDoc?.error?.code}
                                            engines={asset.indexDoc?.engines}
                                        />
                                    )}
                                    {/* 미리보기 추출 상태는 여기에 넣지 않는다 — 디자인 가이드라인의
                                        cp-card-items-data-area 배지 슬롯이 DRM·AI·잠금·인덱싱 4칸(86px)으로
                                        고정돼 있어 5번째를 넣을 여백이 없다. 카드 코너 4곳도
                                        체크박스·더보기·아카이브·NEW가 이미 점유한다. (DAM-457) */}
                                </Stack>
                            </>
                        )}
                    </Grid>
                </Stack>
            </CardContent>
        </Card>
    );
};
AssetCard.propTypes = {
    asset: PropTypes.object,
    checkable: PropTypes.bool,
    renderTools: PropTypes.func,
    renderBody: PropTypes.func,
    renderFooter: PropTypes.func,
    showReindex: PropTypes.bool,
    onClickReindex: PropTypes.func,
};

export default React.memo(AssetCard);
